import RefluencedLogo from "@/assets/images/refluenced_logo_with_text.svg";
import RefluencedLogoDark from "@/assets/images/refluenced_logo_with_text_neg.svg";
import UserAvatarPlaceholder from "@/assets/images/user_avatar_placeholder.svg?react";
import SignUpIcon from "@/assets/light_gradient/sign_up.svg?react";
import UserIcon from "@/assets/light_gradient/user.svg?react";
import ThemeSelector from "@/components/Common/ThemeSelector";
import SideDrawer from "@/components/NavBar/SideDrawer";
import {useHelpUrl} from "@/hooks/useNavigationHooks";
import {useTheme} from "@/hooks/useTheme";
import {openTopBarPopoverAtom} from "@/store/jotaiAtoms";
import {Link} from "@tanstack/react-router";
import {useSetAtom} from "jotai";
import type {FC} from "react";
import {useTranslation} from "react-i18next";
import {useMedia, useToggle} from "react-use";

const NavbarLoggedOut: FC = () => {
  const {t} = useTranslation("navigation");
  const {resolvedTheme} = useTheme();
  const helpUrl = useHelpUrl();
  const isMobileView = useMedia("(max-width: 1024px)");
  const setOpenTopBarPopover = useSetAtom(openTopBarPopoverAtom);
  const [isDrawerOpen, toggleDrawerOpen] = useToggle(false);

  const links = (
    <>
      <Link
        to="/explore-brands"
        activeProps={{className: "text-primary-dark-solid"}}
        inactiveProps={{className: "text-neutral-black hover:text-neutral-grey-dark"}}
        className="flex cursor-pointer items-center justify-center text-base font-medium no-underline transition-colors">
        {t("explore_brands")}
      </Link>
      {[
        {href: "/for-influencers", label: t("for_influencers")},
        {href: "/?landing", label: t("for_brands")},
        {href: helpUrl, label: t("help")},
        {href: "/about", label: t("about_us")},
      ].map(({href, label}) => (
        <a
          key={label}
          href={href}
          className="text-neutral-black hover:text-neutral-grey-dark flex cursor-pointer items-center justify-center text-base font-medium no-underline transition-colors">
          {label}
        </a>
      ))}
    </>
  );

  return (
    <nav role="navigation" className="sticky top-0 z-999 flex h-16 shrink-0 items-center justify-between px-6">
      <div className="absolute inset-0 -z-10 bg-white/80 backdrop-blur-md" />
      <Link to="/">
        <img src={resolvedTheme === "dark" ? RefluencedLogoDark : RefluencedLogo} className="h-10" />
      </Link>
      {isMobileView ? (
        <SideDrawer isOpen={isDrawerOpen} toggleOpen={toggleDrawerOpen}>
          <Link
            to="/login"
            activeProps={{className: "text-primary-dark-solid"}}
            inactiveProps={{className: "text-neutral-black hover:text-neutral-grey-dark"}}
            className="flex items-center justify-center text-base font-medium whitespace-nowrap no-underline transition-colors">
            {t("login")}
          </Link>
          <Link
            to="/register"
            activeProps={{className: "text-primary-dark-solid"}}
            inactiveProps={{className: "text-neutral-black hover:text-neutral-grey-dark"}}
            className="flex items-center justify-center text-base font-medium whitespace-nowrap no-underline transition-colors">
            {t("register")}
          </Link>
          <div className="bg-neutral-grey-light h-px w-full" />
          {links}
          <div className="bg-neutral-grey-light h-px w-full" />
          <ThemeSelector />
        </SideDrawer>
      ) : (
        <div className="flex h-full items-center gap-10">
          {links}
          <div className="flex items-center gap-4">
            <ThemeSelector />
            <div
              onMouseEnter={() => setOpenTopBarPopover(null)}
              className="group relative flex items-center justify-center p-2 pr-1">
              <button className="border-neutral-grey-light hover:border-primary-dark-solid flex size-10 items-center justify-center rounded-full border transition-colors">
                <UserAvatarPlaceholder className="size-5.5 shrink-0" />
              </button>
              <ul className="text-primary-black border-neutral-white-dark absolute top-14 right-0 h-auto w-36 origin-top-right scale-0 overflow-hidden rounded-md border bg-white pl-0 text-xs opacity-0 drop-shadow-lg transition-all duration-200 group-hover:scale-100 group-hover:opacity-100">
                <li key="login">
                  <Link
                    className="hover:bg-neutral-white-dark flex items-center gap-2 border-b px-2.5 py-3 font-normal no-underline"
                    to="/login">
                    <UserIcon className="size-4 shrink-0" />
                    <span>{t("login")}</span>
                  </Link>
                </li>
                <li key="register">
                  <Link
                    className="hover:bg-neutral-white-dark flex items-center gap-2 px-2.5 py-3 font-normal no-underline"
                    to="/register">
                    <SignUpIcon className="size-4 shrink-0" />
                    <span>{t("register")}</span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavbarLoggedOut;
